import "./EmailContactRow.scss";
import { useEffect, useRef, useState } from "react";
import cn from "classnames";
import Link from "/Components/Shared/Link/Link";
import CopySimpleIcon from "/Components/Icons/CopySimpleIcon";

// How long the copy icon keeps its "copied" color after a click
const COPIED_FEEDBACK_MS = 2000;

interface EmailContactRowProps {
  email: string;
  displayText?: string;
  // Translated confirmation shown in a tooltip after the email is copied
  copiedText?: string;
  // Translated "Copy" for the copy button's screen reader label
  copyLabel?: string;
}

const EmailContactRow = ({
  email,
  displayText,
  copiedText,
  copyLabel = "Copy",
}: EmailContactRowProps) => {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  const copyEmail = () =>
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      window.clearTimeout(resetTimer.current);
      resetTimer.current = window.setTimeout(
        () => setCopied(false),
        COPIED_FEEDBACK_MS,
      );
    });

  return (
    <div className="email-contact-row">
      <Link
        as="span"
        href={`mailto:${email}`}
        text={displayText ?? email}
        isExternal={false}
        size="x-small"
        contained
        hasUnderline
      />
      <button
        type="button"
        className={cn("email-contact-row__copy-button", {
          "email-contact-row__copy-button--copied": copied,
        })}
        aria-label={`${copyLabel} ${email}`}
        onClick={copyEmail}
      >
        <CopySimpleIcon size="20" thickness="Light" />
      </button>
      {copiedText && (
        <output className="email-contact-row__tooltip">
          {copied && (
            <span className="email-contact-row__tooltip-text">{copiedText}</span>
          )}
        </output>
      )}
    </div>
  );
};

export default EmailContactRow;
